import { authApi } from "@/api/authApi";
import type { User } from "@/types/user";
import { defineStore } from "pinia";
import { ref } from "vue";

const user_storage_label = "4c_user";
const token_storage_label = "4c_token";
const user_ip_label = "4c_user_ip";

export const useAuthenticationStore = defineStore("authentication", () => {
  const user = ref<User | null>(null);
  const token = ref<string | null>(null);
  const loaded = ref(false);
  const userIp = ref(null);

  const initializeFromStorage = () => {
    const storedUser = localStorage.getItem(user_storage_label);
    const storedToken = localStorage.getItem(token_storage_label);
    const storedUserIp = localStorage.getItem(user_ip_label);
    if (storedUser) user.value = JSON.parse(storedUser);
    if (storedToken) token.value = storedToken;
    if (storedUserIp) userIp.value = storedUserIp;
  };

  async function getUserIP() {
    if (userIp.value) {
      return userIp.value;
    }
    try {
      const response = await fetch("https://api64.ipify.org?format=json");
      const data = await response.json();
      userIp.value = data.ip;
      localStorage.setItem(user_ip_label, data.ip);
      return data.ip;
    } catch ({}) {
      userIp.value = null;
      return null;
    }
  }

  const login = (userData: User, accessToken: string) => {
    user.value = userData;
    token.value = accessToken;
    localStorage.setItem(user_storage_label, JSON.stringify(userData));
    localStorage.setItem(token_storage_label, accessToken);
  };

  const updateUser = (userData: User) => {
    user.value = userData;
    localStorage.setItem(user_storage_label, JSON.stringify(userData));
  };

  const refreshUser = async () => {
    await getUserIP();
    const response = await authApi.fetchUser();
    if (response.data?.data) {
      user.value = response.data.data;
      localStorage.setItem(user_storage_label, JSON.stringify(response.data.data));
    } else {
      localStorage.removeItem(user_storage_label);
      localStorage.removeItem(token_storage_label);
      user.value = null;
    }
    loaded.value = true;
  };

  const logout = () => {
    user.value = null;
    token.value = null;
    localStorage.removeItem(user_storage_label);
    localStorage.removeItem(token_storage_label);
  };

  const isAuthenticated = () => {
    return !!token.value;
  };

  initializeFromStorage();

  return {
    user,
    userIp,
    token,
    loaded,
    login,
    refreshUser,
    updateUser,
    logout,
    isAuthenticated,
  };
});
